import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildCanvasSegments,
  CANVAS_HEIGHT,
  layoutCanvasSegments,
  VIEWBOX_WIDTH,
} from '../../apps/cidr-planner/canvasLayout';
import {
  getGapColor,
  getGapHoverColor,
  getSubnetColor,
  getSubnetLabelColor,
} from '../../apps/cidr-planner/subnetColors';
import type { AddressRange, CidrBlock } from '../../apps/cidr-planner/types';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';
import './CidrCanvas.css';

type CidrCanvasProps = {
  parentCidr: string;
  blocks: CidrBlock[];
  lang: DisplayLang;
  onGapClick: (gap: AddressRange) => void;
  onBlockClick: (block: CidrBlock) => void;
};

const LABELS = {
  ja: { gap: '空き', zoomIn: '拡大', zoomOut: '縮小', reset: 'リセット' },
  en: { gap: 'Free', zoomIn: 'Zoom in', zoomOut: 'Zoom out', reset: 'Reset' },
} as const;

const PAN_CLICK_THRESHOLD = 4;

function segmentLabelFontSize(width: number, height: number, viewBoxSize: number): number {
  const maxSize = viewBoxSize * 0.08;
  const minSize = viewBoxSize * 0.02;
  return Math.max(minSize, Math.min(width * 0.13, height * 0.22, maxSize));
}

function shouldShowCidrLabel(width: number, height: number, viewBoxSize: number): boolean {
  return width >= viewBoxSize * 0.072 && height >= viewBoxSize * 0.036;
}

function shouldShowPrefixLabel(width: number, height: number, viewBoxSize: number): boolean {
  return width >= viewBoxSize * 0.036 || height >= viewBoxSize * 0.028;
}

function shouldShowGapLabel(width: number, height: number, viewBoxSize: number): boolean {
  return width >= viewBoxSize * 0.04 || height >= viewBoxSize * 0.028;
}

type PanState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  active: boolean;
};

export function CidrCanvas({
  parentCidr,
  blocks,
  lang,
  onGapClick,
  onBlockClick,
}: CidrCanvasProps) {
  const labels = LABELS[lang];
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);
  const panRef = useRef<PanState | null>(null);
  const suppressClickRef = useRef(false);
  const viewStateRef = useRef({ scale: 1, pan: { x: 0, y: 0 } });
  viewStateRef.current = { scale, pan };

  const layout = useMemo(() => {
    const built = buildCanvasSegments(parentCidr, blocks);
    return layoutCanvasSegments(built, parentCidr, blocks);
  }, [parentCidr, blocks]);

  const segments = layout.segments;
  const viewBoxSize = layout.viewBoxWidth || VIEWBOX_WIDTH;

  const resetView = useCallback(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const element = innerRef.current;
    if (!element) {
      return undefined;
    }

    const onWheel = (event: WheelEvent) => {
      const { scale: currentScale, pan: currentPan } = viewStateRef.current;
      if (currentScale <= 1 && currentPan.x === 0 && currentPan.y === 0) {
        return;
      }

      event.preventDefault();
      setPan((current) => ({
        x: current.x - event.deltaX,
        y: current.y - event.deltaY,
      }));
    };

    const onTouchStart = (event: TouchEvent) => {
      const { scale: currentScale, pan: currentPan } = viewStateRef.current;

      if (event.touches.length === 2) {
        panRef.current = null;
        setIsDragging(false);
        const [a, b] = [event.touches[0], event.touches[1]];
        const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        pinchRef.current = { distance, scale: currentScale };
        return;
      }

      const zoomed = currentScale > 1 || currentPan.x !== 0 || currentPan.y !== 0;
      if (event.touches.length !== 1 || !zoomed) {
        return;
      }

      const touch = event.touches[0];
      panRef.current = {
        pointerId: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        originX: currentPan.x,
        originY: currentPan.y,
        active: false,
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length === 2 && pinchRef.current) {
        event.preventDefault();
        const [a, b] = [event.touches[0], event.touches[1]];
        const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const ratio = distance / pinchRef.current.distance;
        setScale(Math.min(3, Math.max(0.5, pinchRef.current.scale * ratio)));
        return;
      }

      const activePan = panRef.current;
      if (!activePan || event.touches.length !== 1 || pinchRef.current) {
        return;
      }

      const touch = event.touches[0];
      if (touch.identifier !== activePan.pointerId) {
        return;
      }

      const dx = touch.clientX - activePan.startX;
      const dy = touch.clientY - activePan.startY;
      if (!activePan.active) {
        if (Math.hypot(dx, dy) < PAN_CLICK_THRESHOLD) {
          return;
        }
        activePan.active = true;
        setIsDragging(true);
      }

      event.preventDefault();
      setPan({
        x: activePan.originX + dx,
        y: activePan.originY + dy,
      });
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (event.touches.length >= 2) {
        return;
      }

      pinchRef.current = null;

      const activePan = panRef.current;
      if (!activePan) {
        return;
      }

      suppressClickRef.current = activePan.active;
      panRef.current = null;
      setIsDragging(false);
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd, { passive: true });
    element.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }
    if (pinchRef.current) {
      return;
    }

    const zoomed = scale > 1 || pan.x !== 0 || pan.y !== 0;
    if (!zoomed) {
      return;
    }

    panRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: pan.x,
      originY: pan.y,
      active: false,
    };
  }, [pan.x, pan.y, scale]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const activePan = panRef.current;
    if (!activePan || activePan.pointerId !== event.pointerId || pinchRef.current) {
      return;
    }

    const dx = event.clientX - activePan.startX;
    const dy = event.clientY - activePan.startY;
    if (!activePan.active) {
      if (Math.hypot(dx, dy) < PAN_CLICK_THRESHOLD) {
        return;
      }
      activePan.active = true;
      setIsDragging(true);
      if (typeof event.currentTarget.setPointerCapture === 'function') {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    }

    event.preventDefault();
    setPan({
      x: activePan.originX + dx,
      y: activePan.originY + dy,
    });
  }, []);

  const endPan = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const activePan = panRef.current;
    if (!activePan || activePan.pointerId !== event.pointerId) {
      return;
    }

    if (
      typeof event.currentTarget.hasPointerCapture === 'function'
      && event.currentTarget.hasPointerCapture(event.pointerId)
      && typeof event.currentTarget.releasePointerCapture === 'function'
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    suppressClickRef.current = activePan.active;
    panRef.current = null;
    setIsDragging(false);
  }, []);

  const handleSegmentClick = useCallback(<T,>(action: (value: T) => void, value: T) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    action(value);
  }, []);

  return (
    <div className="cidr-canvas-wrap">
      <div
        ref={innerRef}
        className={`cidr-canvas-wrap__inner${isDragging ? ' cidr-canvas-wrap__inner--dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPan}
        onPointerCancel={endPan}
      >
        <div
          className="cidr-canvas-wrap__content"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <svg
            className="cidr-canvas"
            viewBox={`0 0 ${layout.viewBoxWidth || VIEWBOX_WIDTH} ${layout.viewBoxHeight || CANVAS_HEIGHT}`}
            role="img"
            aria-label={parentCidr}
          >
          <g className="cidr-canvas__grid" aria-hidden>
            {Array.from({ length: layout.grid.rows + 1 }, (_, row) => (
              <line
                key={`h-${row}`}
                x1={10}
                y1={10 + row * layout.cellSize}
                x2={layout.viewBoxWidth - 10}
                y2={10 + row * layout.cellSize}
                stroke="#e5e7eb"
                strokeWidth={1}
              />
            ))}
            {Array.from({ length: layout.grid.cols + 1 }, (_, col) => (
              <line
                key={`v-${col}`}
                x1={10 + col * layout.cellSize}
                y1={10}
                x2={10 + col * layout.cellSize}
                y2={layout.viewBoxHeight - 10}
                stroke="#e5e7eb"
                strokeWidth={1}
              />
            ))}
          </g>
          {segments.map((segment) => {
            const isHovered = hoveredId === segment.id;
            const centerX = segment.x + segment.width / 2;
            const centerY = segment.y + segment.height / 2;
            const cornerRadius = Math.min(8, segment.width / 4, segment.height / 4);
            if (segment.kind === 'gap') {
              const fill = isHovered ? getGapHoverColor() : getGapColor();
              return (
                <g key={segment.id}>
                  <rect
                    className="cidr-canvas__segment cidr-canvas__segment--gap"
                    x={segment.x}
                    y={segment.y}
                    width={segment.width}
                    height={segment.height}
                    rx={cornerRadius}
                    fill={fill}
                    stroke="#d1d5db"
                    onClick={() => handleSegmentClick(onGapClick, segment.range)}
                    onMouseEnter={() => setHoveredId(segment.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  />
                  {shouldShowGapLabel(segment.width, segment.height, viewBoxSize) && (
                    <text
                      className="cidr-canvas__label cidr-canvas__label--dark"
                      x={centerX}
                      y={centerY}
                      fontSize={segmentLabelFontSize(segment.width, segment.height, viewBoxSize)}
                    >
                      {labels.gap}
                    </text>
                  )}
                </g>
              );
            }

            const fill = getSubnetColor(segment.prefix);
            const labelColor = getSubnetLabelColor(segment.prefix);
            const showCidr = shouldShowCidrLabel(segment.width, segment.height, viewBoxSize);
            const showPrefix = shouldShowPrefixLabel(segment.width, segment.height, viewBoxSize);
            const labelSize = segmentLabelFontSize(segment.width, segment.height, viewBoxSize);
            const nameOffset = labelSize * 0.45;
            return (
              <g key={segment.id}>
                <rect
                  className="cidr-canvas__segment cidr-canvas__segment--block"
                  x={segment.x}
                  y={segment.y}
                  width={segment.width}
                  height={segment.height}
                  rx={cornerRadius}
                  fill={fill}
                  stroke="rgba(255,255,255,0.35)"
                  strokeWidth={1.5}
                  onClick={() => handleSegmentClick(onBlockClick, segment.block)}
                  onMouseEnter={() => setHoveredId(segment.id)}
                  onMouseLeave={() => setHoveredId(null)}
                />
                {showCidr ? (
                  <text
                    className="cidr-canvas__label"
                    fill={labelColor}
                    x={centerX}
                    y={centerY - (segment.block.name ? nameOffset : 0)}
                    fontSize={labelSize}
                  >
                    {segment.block.cidr}
                  </text>
                ) : showPrefix ? (
                  <text
                    className="cidr-canvas__label"
                    fill={labelColor}
                    x={centerX}
                    y={centerY}
                    fontSize={labelSize}
                  >
                    /{segment.prefix}
                  </text>
                ) : null}
                {showCidr && segment.block.name && (
                  <text
                    className="cidr-canvas__label"
                    fill={labelColor}
                    x={centerX}
                    y={centerY + nameOffset}
                    fontSize={labelSize * 0.85}
                  >
                    {segment.block.name}
                  </text>
                )}
              </g>
            );
          })}
          </svg>
        </div>
      </div>
      <div className="cidr-canvas-wrap__zoom-controls">
        <button
          type="button"
          className="cidr-canvas-wrap__zoom-btn"
          onClick={() => setScale((current) => Math.min(3, current + 0.2))}
          aria-label={labels.zoomIn}
        >
          +
        </button>
        <button
          type="button"
          className="cidr-canvas-wrap__zoom-btn"
          onClick={() => setScale((current) => Math.max(0.5, current - 0.2))}
          aria-label={labels.zoomOut}
        >
          −
        </button>
        <button
          type="button"
          className="cidr-canvas-wrap__zoom-btn"
          onClick={resetView}
          aria-label={labels.reset}
        >
          ↺
        </button>
      </div>
    </div>
  );
}
